import type { ICellRendererComp, ICellRendererParams } from 'ag-grid-community'

type MarkdownData =
  | {
      readonly type: 'markdown'
      readonly value: string
      readonly html: string
    }
  | string
  | null
  | undefined

const isMarkdownHtml = (
  data: MarkdownData
): data is Extract<MarkdownData, { readonly type: 'markdown' }> =>
  data !== null &&
  typeof data === 'object' &&
  data.type === 'markdown' &&
  typeof data.html === 'string' &&
  typeof data.value === 'string'

const markdownText = (data: MarkdownData): string => {
  if (isMarkdownHtml(data)) {
    return data.value
  }

  return String(data ?? '')
}

const renderMarkdown = (root: HTMLElement, data: MarkdownData): void => {
  root.className = 'markdown-format prose prose-sm max-w-none'
  root.dataset.component = '.grid-markdown'
  root.setAttribute('title', markdownText(data))

  if (isMarkdownHtml(data)) {
    root.innerHTML = data.html
    return
  }

  root.textContent = markdownText(data)
}

export class MarkdownComponent implements ICellRendererComp {
  private el: HTMLDivElement | undefined

  init(params: ICellRendererParams): void {
    this.el = document.createElement('div')
    renderMarkdown(this.el, params.value as MarkdownData)
  }

  getGui(): HTMLElement {
    return this.el ?? document.createElement('div')
  }

  refresh(params: ICellRendererParams): boolean {
    if (!this.el) {
      return false
    }

    renderMarkdown(this.el, params.value as MarkdownData)
    return true
  }

  destroy(): void {
    this.el = undefined
  }
}

export type { MarkdownData }
